import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { Folder, FolderState } from '@/types/folder';
import { RootState } from '@/store';
import { HttpClient } from '@/api/services/http';
import { folderService } from './folderService';
import { fetchContents } from '../content/contentSlice';
import type { Content } from '@/types/content';

// Re-export selectors from the selectors file
export * from './folderSelectors';

// Mock data for initial folders
const initialFolders: Folder[] = [
  {
    id: 'all',
    name: 'All Content',
    parentId: null,
    itemCount: 5,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'folder-1',
    name: 'Product Docs',
    parentId: null,
    itemCount: 1,
    createdAt: '2025-04-22T10:00:00Z',
    updatedAt: '2025-04-22T10:00:00Z',
  },
  {
    id: 'folder-2',
    name: 'Support Playbooks',
    parentId: null,
    itemCount: 1,
    createdAt: '2025-04-20T08:00:00Z',
    updatedAt: '2025-04-20T08:00:00Z',
  },
  {
    id: 'folder-3',
    name: 'Pricing & Legal',
    parentId: null,
    itemCount: 1,
    createdAt: '2025-04-18T12:30:00Z',
    updatedAt: '2025-04-18T12:30:00Z',
  },
  {
    id: 'folder-1-1',
    name: 'API Documentation',
    parentId: 'folder-1',
    itemCount: 0,
    createdAt: '2025-04-22T11:00:00Z',
    updatedAt: '2025-04-22T11:00:00Z',
  },
  {
    id: 'folder-1-2',
    name: 'User Guides',
    parentId: 'folder-1',
    itemCount: 0,
    createdAt: '2025-04-22T12:00:00Z',
    updatedAt: '2025-04-22T12:00:00Z',
  }
];

const initialState: FolderState = {
  items: initialFolders,
  loading: false,
  error: null,
  currentFolderId: 'all', // Default to 'All Content'
  selectedFolderIds: [],
};

// Async thunks for folder operations
export const fetchFolders = createAsyncThunk(
  'folder/fetchFolders',
  async (_, { rejectWithValue }) => {
    try {
      // In a real app, this would be an API call
      const response = await folderService.fetchFolders();
      return response;
    } catch (error) {
      return rejectWithValue('Failed to fetch folders');
    }
  }
);

export const createFolder = createAsyncThunk(
  'folder/createFolder',
  async ({ name, parentId = null }: { name: string; parentId?: string | null }, { rejectWithValue }) => {
    try {
      // In a real app, this would be an API call
      const newFolder: Folder = {
        id: uuidv4(),
        name,
        parentId,
        itemCount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      const response = await HttpClient.apiClient.post("/content-folder", newFolder);
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to create folder');
    }
  }
);

export const renameFolder = createAsyncThunk(
  'folder/renameFolder',
  async ({ id, name }: { id: string; name: string }, { rejectWithValue }) => {
    try {
      // In a real app, this would be an API call
      return { id, name };
    } catch (error) {
      return rejectWithValue('Failed to rename folder');
    }
  }
);

export const deleteFolder = createAsyncThunk(
  'folder/deleteFolder',
  async (id: string, { rejectWithValue, getState }) => {
    try {
      // In a real app, this would be an API call
      // We'd need to check if the folder has content and move it if needed
      return id;
    } catch (error) {
      return rejectWithValue('Failed to delete folder');
    }
  }
);

export const moveContentToFolder = createAsyncThunk(
  'folder/moveContentToFolder',
  async ({ contentIds, folderId }: { contentIds: string[]; folderId: string }, { rejectWithValue }) => {
    try {
      // In a real app, this would be an API call
      return { contentIds, folderId };
    } catch (error) {
      return rejectWithValue('Failed to move content to folder');
    }
  }
);

export const moveFolderToParent = createAsyncThunk(
  'folder/moveFolderToParent',
  async ({ folderId, newParentId }: { folderId: string; newParentId: string | null }, { rejectWithValue, getState }) => {
    try {
      // In a real app, this would be an API call to update the folder's parent
      // For this mock implementation, we'll validate to prevent circular references
      const state = getState() as RootState;
      const folders = state.folder.items;
      
      const folder = folders.find(f => f.id === folderId);
      if (!folder) {
        return rejectWithValue('Folder not found');
      }
      
      // Validate that the move won't create a circular reference
      if (newParentId) {
        const isChildFolder = (parentId: string, childId: string): boolean => {
          const children = folders.filter(f => f.parentId === parentId);
          
          if (children.some(child => child.id === childId)) {
            return true;
          }
          
          return children.some(child => isChildFolder(child.id, childId));
        };
        
        // Check if the new parent is actually a child of the folder we're moving
        if (isChildFolder(folderId, newParentId)) {
          return rejectWithValue('Cannot move a folder into its own subfolder');
        }
      }
      
      return { folderId, newParentId };
    } catch (error) {
      return rejectWithValue('Failed to move folder');
    }
  }
);

const folderSlice = createSlice({
  name: 'folder',
  initialState,
  reducers: {
    setCurrentFolder: (state, action: PayloadAction<string | null>) => {
      state.currentFolderId = action.payload;
    },
    selectFolder: (state, action: PayloadAction<string>) => {
      if (!state.selectedFolderIds.includes(action.payload)) {
        state.selectedFolderIds.push(action.payload);
      }
    },
    deselectFolder: (state, action: PayloadAction<string>) => {
      state.selectedFolderIds = state.selectedFolderIds.filter(id => id !== action.payload);
    },
    clearFolderSelection: (state) => {
      state.selectedFolderIds = [];
    },
    incrementFolderItemCount: (state, action: PayloadAction<string>) => {
      const folder = state.items.find(f => f.id === action.payload);
      if (folder) {
        folder.itemCount += 1;
        folder.updatedAt = new Date().toISOString();
      }
    },
    decrementFolderItemCount: (state, action: PayloadAction<string>) => {
      const folder = state.items.find(f => f.id === action.payload);
      if (folder && folder.itemCount > 0) {
        folder.itemCount -= 1;
        folder.updatedAt = new Date().toISOString();
      }
    }
  },
  extraReducers: (builder) => {
    // Fetch folders
    builder
      .addCase(fetchFolders.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchFolders.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        const totalFromPayload = state.items.reduce((sum, folder) => {
          const value = Number(folder.itemCount ?? 0);
          return Number.isNaN(value) ? sum : sum + value;
        }, 0);
        const allFolder = state.items.find(folder => folder.id === 'all');
        if (!allFolder) {
          state.items.unshift({
            id: 'all',
            name: 'All Content',
            parentId: null,
            itemCount: totalFromPayload,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });
        } else {
          allFolder.itemCount = totalFromPayload;
          allFolder.updatedAt = new Date().toISOString();
        }
        state.error = null;
      })
      .addCase(fetchFolders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder.addCase(fetchContents.fulfilled, (state, action) => {
      const { items: contents, meta } = action.payload;
      const totalsByFolder = new Map<string, number>();

      contents.forEach(content => {
        if (!content.folderId) {
          return;
        }
        totalsByFolder.set(content.folderId, (totalsByFolder.get(content.folderId) ?? 0) + 1);
      });

      const totalCount = typeof meta?.total === 'number' ? meta.total : contents.length;

      state.items.forEach(folder => {
        if (folder.id === 'all') {
          folder.itemCount = totalCount;
          folder.updatedAt = new Date().toISOString();
          return;
        }

        const count = totalsByFolder.get(folder.id) ?? 0;
        folder.itemCount = count;
        folder.updatedAt = new Date().toISOString();
      });
    });
    
    // Create folder
    builder
      .addCase(createFolder.fulfilled, (state, action) => {
        state.items.push(action.payload);
        state.error = null;
      })
      .addCase(createFolder.rejected, (state, action) => {
        state.error = action.payload as string;
      });
    
    // Rename folder
    builder
      .addCase(renameFolder.fulfilled, (state, action) => {
        const folder = state.items.find(f => f.id === action.payload.id);
        if (folder) {
          folder.name = action.payload.name;
          folder.updatedAt = new Date().toISOString();
        }
        state.error = null;
      })
      .addCase(renameFolder.rejected, (state, action) => {
        state.error = action.payload as string;
      });
    
    // Delete folder
    builder
      .addCase(deleteFolder.fulfilled, (state, action) => {
        state.items = state.items.filter(f => f.id !== action.payload);
        if (state.currentFolderId === action.payload) {
          state.currentFolderId = 'all'; // Reset to All Content
        }
        state.error = null;
      })
      .addCase(deleteFolder.rejected, (state, action) => {
        state.error = action.payload as string;
      });
      
    // Move content to folder
    builder
      .addCase(moveContentToFolder.fulfilled, (state, action) => {
        // This would handle updating item count in real implementation
        state.error = null;
      })
      .addCase(moveContentToFolder.rejected, (state, action) => {
        state.error = action.payload as string;
      });
      
    // Move folder to parent
    builder
      .addCase(moveFolderToParent.pending, (state) => {
        state.loading = true;
      })
      .addCase(moveFolderToParent.fulfilled, (state, action) => {
        const folder = state.items.find(f => f.id === action.payload.folderId);
        if (folder) {
          folder.parentId = action.payload.newParentId;
          folder.updatedAt = new Date().toISOString();
        }
        state.error = null;
        state.loading = false;
      })
      .addCase(moveFolderToParent.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      });
  },
});

// Export actions
export const { 
  setCurrentFolder, 
  selectFolder, 
  deselectFolder, 
  clearFolderSelection,
  incrementFolderItemCount,
  decrementFolderItemCount
} = folderSlice.actions;

export default folderSlice.reducer;
